import { useEffect, useState } from "react";
import { api, MEDIA } from "../lib/api";

/**
 * Half-letter flyer, 2-up per US Letter page.
 * Paper: 8.5" × 11" PORTRAIT, print double-sided, flip on LONG edge.
 * Cut horizontally in half → two identical flyers.
 */
export default function Brochure() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="p-10">Loading…</div>;

  const WEBSITE_URL = "https://stone-shepherd.com";
  const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(WEBSITE_URL)}&margin=0&color=232323`;

  const Lamb = ({ size = 130 }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden>
      <path d="M14 38 C 10 34, 12 26, 20 26 C 22 20, 32 20, 34 26 C 40 24, 48 28, 46 34 C 50 36, 48 44, 42 44 L 18 44 C 12 44, 10 40, 14 38 Z"
        fill="#EBE8E0" stroke="#3F5746" strokeWidth="1.5" />
      <ellipse cx="42" cy="30" rx="6" ry="7" fill="#3F5746" />
      <path d="M44 24 C 46 22, 49 23, 48 26" stroke="#3F5746" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <circle cx="44" cy="30" r="1" fill="#F7F6F2" />
      <line x1="20" y1="44" x2="20" y2="52" stroke="#3F5746" strokeWidth="2" strokeLinecap="round" />
      <line x1="28" y1="44" x2="28" y2="52" stroke="#3F5746" strokeWidth="2" strokeLinecap="round" />
      <line x1="36" y1="44" x2="36" y2="52" stroke="#3F5746" strokeWidth="2" strokeLinecap="round" />
      <line x1="42" y1="44" x2="42" y2="52" stroke="#3F5746" strokeWidth="2" strokeLinecap="round" />
      <path d="M14 54 L 18 51 L 22 54 L 26 51 L 30 54 L 34 51 L 38 54 L 42 51 L 46 54"
        stroke="#3F5746" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </svg>
  );

  const Headstone = ({ size = 130 }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
      <path
        d="M16 3 C 10 3, 6 7, 6 14 L 6 28 L 26 28 L 26 14 C 26 7, 22 3, 16 3 Z"
        stroke="#F7F6F2"
        strokeWidth="1.8"
        fill="none"
      />
      <line x1="16" y1="10" x2="16" y2="21" stroke="#F7F6F2" strokeWidth="1.4" />
      <line x1="11" y1="14" x2="21" y2="14" stroke="#F7F6F2" strokeWidth="1.4" />
    </svg>
  );

  const FlyerFront = () => (
    <div className="flyer flyer-front">
      <div className="left-col">
        <Headstone size={110} />
        <div className="serif brand-name">Stone Shepherd</div>
        <div className="overline light">Memorial Restoration</div>
        <div className="overline light small-loc">{cfg.business_location}</div>

        <div className="divider-h" />

        <p className="lede">
          Careful, non-destructive cleaning of historic and modern
          headstones. D/2 Biological Solution — approved by the National
          Park Service.
        </p>

        <div className="contact-bar">
          <div className="serif" style={{ fontSize: 18, color: "#F7F6F2", marginBottom: 4 }}>
            {cfg.business_phone}
          </div>
          <div>{cfg.business_email}</div>
          <div>stone-shepherd.com</div>
        </div>
      </div>

      <div className="right-col">
        <div className="overline">Pricing</div>
        <div className="divider-sm" />

        {Object.entries(cfg.size_tiers).map(([k, v]) => (
          <div key={k} className="price-row">
            <div>
              <div className="serif" style={{ fontSize: 18, textTransform: "capitalize", lineHeight: 1.1 }}>
                {k}
              </div>
              <div style={{ fontSize: 10, color: "#5C5C5C", marginTop: 3 }}>{v.label}</div>
            </div>
            <div className="serif price-num">
              ${v.price}{k === "large" ? "+" : ""}
            </div>
          </div>
        ))}

        <p style={{ fontSize: 9.5, color: "#5C5C5C", marginTop: 8, lineHeight: 1.5 }}>
          Prices are a starting point. Final quotes account for stone
          condition and accessibility.
        </p>
      </div>
    </div>
  );

  const FlyerBack = () => (
    <div className="flyer flyer-back">
      <div className="back-left">
        <div className="overline">Before &amp; After</div>
        <div className="serif" style={{ fontSize: 22, lineHeight: 1.1, marginTop: 6, marginBottom: 12 }}>
          Every stone tells a story.
        </div>
        <div className="ba-grid">
          <div>
            <img src={MEDIA.after} alt="Before" />
            <div className="ba-label">Before</div>
          </div>
          <div>
            <img src={MEDIA.before} alt="After" />
            <div className="ba-label">After</div>
          </div>
        </div>
        <div className="quote-block">
          <div className="serif" style={{ fontSize: 14, lineHeight: 1.3 }}>
            "The kindest cleaner I've ever used on marble."
          </div>
          <div className="overline" style={{ marginTop: 6 }}>— National Park Service</div>
        </div>
      </div>

      <div className="back-right">
        <Lamb size={110} />
        <div className="overline" style={{ textAlign: "center", marginTop: 4 }}>Scan to request a quote</div>
        <div className="qr-wrap">
          <img src={QR_URL} alt="QR to stone-shepherd.com" />
        </div>
        <div className="serif" style={{ fontSize: 15, textAlign: "center" }}>
          stone-shepherd.com
        </div>

        <div className="contact-card">
          <div className="overline light">Get in touch</div>
          <p style={{ fontSize: 13, marginTop: 6, lineHeight: 1.55 }}>
            <strong>{cfg.business_phone}</strong>
            <br />{cfg.business_email}
            <br />Venmo · @{cfg.venmo_username}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="brochure-root">
      <style>{`
        @page { size: 8.5in 11in; margin: 0; }
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Manrope:wght@300;400;500;600&display=swap');

        .brochure-root {
          background: #555; font-family: 'Manrope', sans-serif; color: #232323;
          padding: 24px; min-height: 100vh;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .brochure-root *, .brochure-root *::before, .brochure-root *::after {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .toolbar { max-width: 900px; margin: 0 auto 20px; display:flex; gap:12px; justify-content:space-between; align-items:center; color:#F7F6F2; }
        .toolbar h2 { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 500; margin:0; }
        .toolbar button { background:#3F5746; color:#fff; border:0; padding:10px 18px; font-size:12px; letter-spacing:.15em; text-transform:uppercase; cursor:pointer; }

        .sheet {
          width: 8.5in; height: 11in;
          background: #F7F6F2;
          margin: 0 auto 24px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.35);
          display: flex; flex-direction: column;
          position: relative;
        }
        .sheet + .sheet {
          page-break-before: always;
          break-before: page;
        }
        .flyer {
          width: 100%; height: 5.5in;
          display: grid; grid-template-columns: 1fr 1fr;
          overflow: hidden; position: relative;
        }
        .serif { font-family: 'Cormorant Garamond', serif; font-weight: 500; letter-spacing: -0.01em; }
        .overline { font-size: 9.5px; letter-spacing: 0.24em; text-transform: uppercase; color: #98604A; }
        .overline.light { color: #DCD9CE; }
        .divider-h { width: 44px; height: 1px; background: #98604A; margin: 16px 0; }
        .divider-sm { width: 28px; height: 1px; background: #98604A; margin: 4px 0 12px; }

        /* ---------- FRONT ---------- */
        .flyer-front .left-col {
          background: #3F5746; color: #F7F6F2;
          padding: 0.35in 0.4in;
          display: flex; flex-direction: column;
          align-items: flex-start;
        }
        .flyer-front .right-col {
          background: #F7F6F2;
          padding: 0.4in 0.4in;
          display: flex; flex-direction: column;
        }
        .brand-name {
          font-size: 34px; color: #F7F6F2; line-height: 1;
          margin-top: 8px;
        }
        .small-loc { font-size: 8.5px; margin-top: 2px; }
        .lede { font-size: 11.5px; line-height: 1.6; color: #DCD9CE; max-width: 92%; }
        .contact-bar {
          margin-top: auto;
          border-top: 1px solid rgba(220,217,206,0.25);
          padding-top: 12px;
          font-size: 11px; line-height: 1.55; color: #DCD9CE;
          width: 100%;
        }
        .price-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 12px 0; border-bottom: 1px solid #DCD9CE;
        }
        .price-num { font-size: 28px; }

        /* ---------- BACK ---------- */
        .flyer-back {
          background: #F7F6F2;
          padding: 0.4in;
          gap: 0.35in;
        }
        .back-left { display: flex; flex-direction: column; }
        .back-right {
          display: flex; flex-direction: column;
          align-items: center;
        }
        .ba-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px; }
        .ba-grid img { width: 100%; height: 1.7in; object-fit: cover; object-position: 50% 40%; display: block; }
        .ba-label { text-align: center; font-size: 9.5px; letter-spacing: 0.25em; text-transform: uppercase; color: #98604A; margin-top: 5px; }
        .quote-block {
          border-left: 2px solid #98604A;
          padding: 6px 0 6px 12px;
          margin-top: auto;
        }
        .qr-wrap {
          padding: 12px; background: #fff; border: 1px solid #DCD9CE;
          margin: 10px 0 8px;
        }
        .qr-wrap img { display: block; width: 1.9in; height: 1.9in; }
        .contact-card {
          background: #3F5746; color: #F7F6F2;
          padding: 14px 16px;
          margin-top: auto;
          width: 100%;
          text-align: center;
          box-sizing: border-box;
        }

        @media print {
          .brochure-root { background: #fff; padding: 0; }
          .toolbar { display: none; }
          .print-instructions { display: none; }
          .sheet { box-shadow: none; margin: 0; }
        }
      `}</style>

      <div className="toolbar">
        <h2>Flyer — 2 per page (cut horizontally)</h2>
        <button onClick={() => window.print()} data-testid="brochure-print">Print flyer</button>
      </div>

      <div className="sheet" data-testid="flyer-page-front">
        <FlyerFront />
        <FlyerFront />
      </div>

      <div className="sheet" data-testid="flyer-page-back">
        <FlyerBack />
        <FlyerBack />
      </div>

      <div className="print-instructions" style={{ maxWidth: 900, margin: "0 auto", color: "#DCD9CE", fontSize: 12, lineHeight: 1.6 }}>
        <strong style={{ color: "#fff" }}>Print instructions:</strong> US Letter, portrait,
        double-sided, flip on <strong>long edge</strong>. Margins: <strong>None</strong>.
        <strong> Background graphics: ON</strong>. Cut horizontally along the center — two identical flyers.
      </div>
    </div>
  );
}
